import api from './client';

export interface XeroStatus {
  connected: boolean;
  tenantName?: string;
  connectedAt?: string;
  connectedByEmail?: string;
  lastContactSync?: string | null;
  lastStatusSync?: string | null;
}

export interface XeroSyncResult {
  synced: number;
  suppliersLinked?: number;
  skipped: boolean;
}

export interface XeroPurchaseOrdersResult {
  created: { supplier: string; purchaseOrderId: string; poNumber: string; updated: boolean }[];
  skipped: { supplier: string; reason: string }[];
}

export interface XeroStatusSyncResult {
  skipped: boolean;
  quotesChecked: number;
  quotesUpdated: { quoteId: string; from: string; to: string }[];
  posChecked: number;
  posUpdated: { poNumber: string; from: string | null; to: string }[];
}

/** One row of the Xero chart of accounts, for the account-code picker. */
export interface XeroChartAccount {
  code: string;
  name: string;
  /** REVENUE | EXPENSE | ASSET | LIABILITY | EQUITY */
  accountClass: string;
  /** Finer Xero type, e.g. INVENTORY, DIRECTCOSTS, REVENUE */
  type: string;
  archived: boolean;
}

export interface XeroAccountsResult {
  accounts: XeroChartAccount[];
  /** Why the list is empty, or null when it isn't. Never an error. */
  unavailable: string | null;
}

export interface XeroAccountCodes {
  income: {
    equipment: string;
    sub_hire: string;
    labour: string;
    transport: string;
    other: string;
    sales: string;
  };
  purchase: {
    sub_hire: string;
    sales: string;
    stock: string;
  };
  inventory: {
    asset: string;
    cogs: string;
  };
}

/** One stock-on-hand journal sent to Xero. */
export interface StockJournalRun {
  id: string;
  periodEnd: string;
  source: 'weekly' | 'manual' | 'opening';
  status: 'posted' | 'draft' | 'skipped' | 'failed';
  appStockValue: string;
  xeroBalance: string;
  amount: string;
  xeroJournalId: string | null;
  error: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface StockJournalPlan {
  /** null = ready to post; otherwise the one reason it can't. */
  blocked: string | null;
  periodEnd: string;
  appStockValue: number;
  stockItems: number;
  xeroBalance: number;
  amount: number;
  /** The app holds more stock than Xero — usually a bill not entered yet. */
  reversed: boolean;
  stockAccount: string;
  cogsAccount: string;
  alreadyPosted: boolean;
}

export interface StockJournalSettings {
  weekly: boolean;
  postDirect: boolean;
}

export interface StockJournalView {
  plan: StockJournalPlan | null;
  settings: StockJournalSettings;
  history: StockJournalRun[];
  weeklyPeriodEnd: string;
  error: string | null;
}

export interface StockJournalResult {
  status: 'posted' | 'draft' | 'skipped' | 'failed';
  amount: number;
  periodEnd: string;
  xeroJournalId?: string | null;
  message: string;
}

export interface XeroAccountCodesResult {
  codes: XeroAccountCodes;
  /** The codes the app ships with, for "reset to defaults". */
  defaults: XeroAccountCodes;
}

export interface XeroSupplierContact {
  contactID: string;
  name: string;
  /** Created in Xero but never billed or invoiced, so not flagged as a supplier yet. */
  isNew: boolean;
}

export const xeroApi = {
  getStatus: () => api.get<XeroStatus>('/xero/status').then((r) => r.data),
  getAuthUrl: () => api.get<{ url: string }>('/xero/auth').then((r) => r.data.url),
  syncContacts: () => api.post<XeroSyncResult>('/xero/sync/contacts').then((r) => r.data),
  // Used by background/auto triggers — honours the 10-minute cache
  autoSyncContacts: () => api.post<XeroSyncResult>('/xero/sync/contacts/auto').then((r) => r.data),
  disconnect: () => api.post('/xero/disconnect').then((r) => r.data),
  pushQuote: (quoteId: string) =>
    api.post<{ xeroQuoteId: string; xeroQuoteNumber: string }>(`/xero/push/${quoteId}`).then((r) => r.data),
  pushPurchaseOrders: (quoteId: string) =>
    api.post<XeroPurchaseOrdersResult>(`/xero/purchase-orders/${quoteId}`).then((r) => r.data),
  // Pull quote/PO statuses from Xero on demand (the backend also polls every 15 min)
  syncStatus: () => api.post<XeroStatusSyncResult>('/xero/sync/status').then((r) => r.data),
  listSupplierContacts: () =>
    api.get<XeroSupplierContact[]>('/xero/suppliers').then((r) => r.data),
  getAccounts: () => api.get<XeroAccountsResult>('/xero/accounts').then((r) => r.data),
  getAccountCodes: () =>
    api.get<XeroAccountCodesResult>('/xero/account-codes').then((r) => r.data),
  saveAccountCodes: (codes: XeroAccountCodes) =>
    api
      .put<{ ok: true; checkedAgainstXero: boolean; changed: number; linkedItemsNotUpdated: number }>(
        '/xero/account-codes',
        codes,
      )
      .then((r) => r.data),
  getStockJournal: (periodEnd?: string) =>
    api
      .get<StockJournalView>('/xero/stock-journal', { params: periodEnd ? { periodEnd } : undefined })
      .then((r) => r.data),
  pushStockJournal: (periodEnd?: string) =>
    api.post<StockJournalResult>('/xero/stock-journal', periodEnd ? { periodEnd } : {}).then((r) => r.data),
  pushOpeningStockJournal: (balancingAccount: string) =>
    api.post<StockJournalResult>('/xero/stock-journal/opening', { balancingAccount }).then((r) => r.data),
  saveStockJournalSettings: (settings: StockJournalSettings) =>
    api.put<StockJournalSettings>('/xero/stock-journal/settings', settings).then((r) => r.data),
  getTrackedInventory: () =>
    api.get<{ enabled: boolean }>('/xero/tracked-inventory').then((r) => r.data),
  setTrackedInventory: (enabled: boolean) =>
    api.put<{ enabled: boolean }>('/xero/tracked-inventory', { enabled }).then((r) => r.data),
  pushOpeningStock: (balancingAccount: string) =>
    api
      .post<{ invoiceId: string; lines: number; total: number }>(
        '/xero/tracked-inventory/opening-stock',
        { balancingAccount },
      )
      .then((r) => r.data),
};
